import React, { useState } from 'react';
import { Modal, Form, Select, Input, Alert, Space, Typography, App } from 'antd';
import { WarningOutlined, SwapOutlined, DollarOutlined, CreditCardOutlined, BankOutlined, MobileOutlined, GiftOutlined } from '@ant-design/icons';
import { OrderPayment } from '../../../types/orders';
import { useGetPaymentMethodsQuery } from '../../../services/paymentMethodsApi';
import { useUpdatePaymentMethodMutation } from '../../../services/ordersApi';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ChangePaymentMethodModalProps {
  visible: boolean;
  orderId: string;
  currentPayment: OrderPayment;
  hasFactura: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangePaymentMethodModal: React.FC<ChangePaymentMethodModalProps> = ({
  visible,
  orderId,
  currentPayment,
  hasFactura,
  onClose,
  onSuccess,
}) => {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Fetch payment methods (activeOnly to get only active methods)
  const { data: paymentMethods = [], isLoading: loadingMethods } = useGetPaymentMethodsQuery({
    activeOnly: false, // Get all to filter manually
  });
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();

  // Filter out current payment method and inactive methods
  const availablePaymentMethods = paymentMethods.filter(
    (pm) => pm.isActive && pm.id !== currentPayment.paymentMethodId
  );

  // Function to get icon based on payment method code
  const getPaymentIcon = (code: string) => {
    switch (code?.toLowerCase()) {
      case 'cash':
      case 'efectivo':
        return <DollarOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
      case 'card':
      case 'tarjeta':
      case 'credit':
      case 'credit_card':
      case 'card_credit':
        return <CreditCardOutlined style={{ color: '#1890ff', fontSize: 18 }} />;
      case 'debit':
      case 'debito':
      case 'debit_card':
      case 'card_debit':
        return <CreditCardOutlined style={{ color: '#fa8c16', fontSize: 18 }} />;
      case 'transfer':
      case 'transferencia':
        return <BankOutlined style={{ color: '#722ed1', fontSize: 18 }} />;
      case 'mobile':
      case 'movil':
        return <MobileOutlined style={{ color: '#13c2c2', fontSize: 18 }} />;
      case 'voucher':
      case 'vale':
        return <GiftOutlined style={{ color: '#eb2f96', fontSize: 18 }} />;
      default:
        return <DollarOutlined style={{ fontSize: 18 }} />;
    }
  };

  const handleSubmit = async (values: { newMethodId: string; reason: string; notes?: string }) => {
    setLoading(true);
    try {
      await updatePaymentMethod({
        orderId,
        paymentId: currentPayment.id,
        newPaymentMethodId: values.newMethodId,
        reason: values.reason,
        notes: values.notes,
      }).unwrap();

      messageApi.success('Método de pago actualizado exitosamente');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al actualizar el método de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          Cambiar Método de Pago
        </Space>
      }
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Confirmar Cambio"
      cancelText="Cancelar"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          newMethodId: undefined,
          reason: '',
          notes: '',
        }}
      >
        {/* Advertencias */}
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Alert
            message="Esta acción afectará el balance de caja"
            description="El monto del pago será restado del método anterior y sumado al nuevo método en la caja registradora."
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />

          {hasFactura && (
            <Alert
              message="Esta orden tiene factura timbrada"
              description="Cambiar el método de pago no afectará la factura, pero debe verificar que el cambio sea correcto."
              type="info"
              showIcon
            />
          )}
        </Space>

        {/* Info del pago actual */}
        <div
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: 12,
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Método actual:</Text>
              <Text strong style={{ marginLeft: 8 }}>
                {currentPayment.paymentMethodName}
              </Text>
            </div>
            <div>
              <Text type="secondary">Monto:</Text>
              <Text strong style={{ marginLeft: 8 }}>
                ${currentPayment.amount.toFixed(2)}
              </Text>
            </div>
            {currentPayment.reference && (
              <div>
                <Text type="secondary">Referencia:</Text>
                <Text style={{ marginLeft: 8 }}>{currentPayment.reference}</Text>
              </div>
            )}
          </Space>
        </div>

        {/* Nuevo método de pago */}
        <Form.Item
          label="Nuevo Método de Pago"
          name="newMethodId"
          rules={[{ required: true, message: 'Selecciona un método de pago' }]}
        >
          <Select
            placeholder="Selecciona el nuevo método"
            loading={loadingMethods}
            disabled={availablePaymentMethods.length === 0}
            size="large"
          >
            {availablePaymentMethods.map((pm) => (
              <Option key={pm.id} value={pm.id}>
                <Space>
                  {getPaymentIcon(pm.code)}
                  <span>{pm.name}</span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {availablePaymentMethods.length === 0 && (
          <Alert
            message="No hay métodos de pago alternativos disponibles"
            type="warning"
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Razón (obligatorio) */}
        <Form.Item
          label="Razón del Cambio"
          name="reason"
          rules={[
            { required: true, message: 'La razón es obligatoria' },
            { min: 5, message: 'La razón debe tener al menos 5 caracteres' },
            { max: 200, message: 'La razón no puede exceder 200 caracteres' },
          ]}
        >
          <Input
            placeholder="Ej: Error al registrar, era efectivo no tarjeta"
            maxLength={200}
            showCount
            size="large"
          />
        </Form.Item>

        {/* Notas (opcional) */}
        <Form.Item
          label="Notas Adicionales (opcional)"
          name="notes"
          rules={[{ max: 500, message: 'Las notas no pueden exceder 500 caracteres' }]}
        >
          <TextArea
            placeholder="Información adicional sobre el cambio..."
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePaymentMethodModal;
